import { getDayjsFromSecomDate } from '@/utils/date';
import { mapEventList, mapEventListSeekbar } from '@/utils/event';
import dayjs from 'dayjs';
import { eventService } from '../../services/event.service';
import pAll from 'p-all';
import delay from '@/utils/delay';
import Vue from 'vue';
import { preloadImage, resizeImage } from '@/utils/helpers';
import { AppAbortController } from '@/utils/abortController';
const state = {
  event: null,
  eventThumbnailUrl: {},
  eventList: [],
  searchEvents: [],
  seekbarEvents: [],
  recordEvents: [],
  homeEventMounted: false,
  firstEventBatchLoaded: false,
  currentSearchRunId: null,
};

// Getter functions
const getters = {
  getEvent() {
    return JSON.parse(JSON.stringify(state.event));
  },
};

// Mutations
const mutations = {
  setEvent(state, data) {
    state.event = data;
  },
  setEventList(state, eventList) {
    state.eventList = eventList;
  },
  addSearchEvents(state, events) {
    state.searchEvents.push(...events);
  },
  addSeekbarEvents(state, events) {
    state.seekbarEvents.push(...events);
  },
  setRecordEvents(state, events) {
    state.recordEvents = events;
  },
  clearSearchEvents(state) {
    state.searchEvents = [];
  },
  clearSeekbarEvents(state) {
    state.seekbarEvents = [];
  },
  setEventThumbnailUrl(state, { eventId, eventThumbnailUrl }) {
    Vue.set(state.eventThumbnailUrl, eventId, eventThumbnailUrl);
  },
  setHomeEventMounted(state, data) {
    state.homeEventMounted = data;
  },
  setFirstEventBatchLoaded(state, data) {
    state.firstEventBatchLoaded = data;
  },
  setCurrentSearchRunId(state, runId) {
    state.currentSearchRunId = runId;
  },
};

// Actions
const actions = {
  changeEvent({ commit }, data) {
    commit('setEvent', data);
  },
  async fetchEventList({ commit }, params) {
    const res = await eventService.getEventList(params);
    commit('setEventList', res.list);
    return !!res.isNextData;
  },
  setHomeEventMounted({ commit }, data) {
    commit('setHomeEventMounted', data);
  },
  setFirstEventBatchLoaded({ commit }, data) {
    commit('setFirstEventBatchLoaded', data);
  },
  setCurrentSearchRunId({ commit }, runId) {
    commit('setCurrentSearchRunId', runId);
  },
  async fetchSearchEvents({ commit, state }, params) {
    const res = await eventService.getEventList(params);

    // if request was canceled or invalid, do not mutate state
    if (!res || res._isCancel || !res.list) {
      return { hasMore: false, list: [] };
    }

    // ignore stale responses: only accept results for the active search run
    if (
      params &&
      params.__searchRunId &&
      state.currentSearchRunId &&
      params.__searchRunId !== state.currentSearchRunId
    ) {
      return { hasMore: false, list: [] };
    }

    // add events for event history sidebar
    commit(
      'addSearchEvents',
      mapEventList(res.list, state.searchEvents.length)
    );

    // add events for main seek-bar
    commit('addSeekbarEvents', mapEventListSeekbar(res.list));

    const result = { hasMore: !!res.isNextData, list: res.list };

    return result;
  },
  async fetchSearchEventImages({ commit }, payload) {
    const controller = AppAbortController.get(payload.abortKey);
    const signal = controller?.signal;
    if (!controller || controller?.signal?.aborted) {
      return Promise.resolve();
    }
    if (!payload.list?.length) {
      // eslint-disable-next-line no-console
      console.warn('No event list to load for event images.');
      return Promise.resolve();
    }
    // new event list
    const currentEventList = payload.list;

    // Event Images only
    const batchSize = 20;
    const totalItems = currentEventList.length;
    const batchProcessingPromises = [];

    // Load 10 items each batch
    for (let i = 0; i < totalItems; i += batchSize) {
      const batch = currentEventList.slice(i, i + batchSize);

      const mappedBatch = [];
      // Create image blob by fetching url
      batch.forEach((event) => {
        mappedBatch.push(() => {
          return preloadImage(event.eventThumbnailUrl)
            .then((img) => {
              if (signal.aborted) {
                return null;
              }
              return resizeImage(img);
            })
            .then((dataUrl) => {
              commit('setEventThumbnailUrl', {
                eventId: event.eventId,
                eventThumbnailUrl: dataUrl,
              });
            })
            .catch((error) => {
              const isAbort =
                error instanceof DOMException ||
                error.name === 'AbortError' ||
                error?.message?.toLowerCase().includes('abort');
              if (!isAbort) {
                // eslint-disable-next-line no-console
                console.error('[Error] fetching event thumbnail url.', error);
                commit('setEventThumbnailUrl', {
                  eventId: event.eventId,
                  eventThumbnailUrl: null,
                });
              }
            });
        });
      });

      // If total items are less than 20, also release loading status
      if (i >= batchSize || totalItems <= batchSize) {
        // Release loading status after the first 20 items loaded
        commit('setFirstEventBatchLoaded', true);
      }

      batchProcessingPromises.push(() => pAll(mappedBatch, { signal }));
      // delay loading image in each batch to avoid poor performance when video stream loading at the same time
      batchProcessingPromises.push(() => delay(i === 0 ? 3000 : 500));
    }

    try {
      await pAll(batchProcessingPromises, { concurrency: 1, signal });
    } catch (error) {
      // p-all/p-map v5+ rejects with DOMException when AbortSignal fires.
      // this is an intentional cancellation — swallow it silently.
      const isAbort =
        error instanceof DOMException ||
        error.name === 'AbortError' ||
        error.name === 'UserUnAuthenticatedException';
      if (!isAbort) {
        throw error;
      }
    }
  },
  async fetchSeekbarEvents({ commit }, params) {
    const res = await eventService.getEventList(params);

    commit('addSeekbarEvents', mapEventList(res.list));
    return !!res.isNextData;
  },
  async fetchRecordEvents({ commit }, params) {
    const res = await eventService.getEventList(params);
    const mappedEvents = mapEventList(res.list);
    commit('setRecordEvents', mappedEvents);
    return mappedEvents;
  },
  async resetSearchEvents({ commit }) {
    commit('clearSearchEvents');
  },
  async clearSeekbarEvents({ commit }) {
    commit('clearSeekbarEvents');
  },
  async fetchCurrentEvent({ commit }, { params, eventId }) {
    const res = await eventService.getEventList(params);
    let event = null;
    if (res.list) {
      event = res.list.find((event) => event.eventId === eventId);
      const startEventUnix = getDayjsFromSecomDate(
        event.eventStartDate
      ).valueOf();
      const endEventUnix = getDayjsFromSecomDate(event.eventEndDate).valueOf();
      event.startEventUnix = startEventUnix;
      event.endEventUnix = endEventUnix;
      let format = 'mm:ss';
      if (dayjs(endEventUnix).diff(dayjs(startEventUnix), 'hours') > 0) {
        format = 'HH:mm:ss';
      }
      event.duration = dayjs
        .duration(dayjs(endEventUnix).diff(dayjs(startEventUnix)))
        .format(format);
      event.suspect =
        event.eventMetaDatas.findIndex(
          (event) => event.value === '不審物の検知'
        ) !== -1;
    }

    commit('setEvent', event);
    return event;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
